import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApiError,
  type MessageChannel,
  type MessageTplRow,
  type MetricDefRow,
  type MetricGroup,
  type ParamValueType,
  type PlatformParamRow,
  type TplKind,
  type TplRow,
  type TplStatus
} from '@hq/api-sdk';
import { formatDisplayTime } from '@hq/utils';
import {
  App,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { hq } from '../session';
import { TipText, sumWidth } from '@hq/ui-kit';

const errText = (err: unknown) => (err instanceof ApiError ? err.friendlyMessage : String(err));

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: '启用' },
  { value: 'DISABLED', label: '停用' }
];

const statusTag = (status: TplStatus) =>
  status === 'ACTIVE' ? <Tag color="green">启用</Tag> : <Tag>停用</Tag>;

/** 各类型模板的填写示例（放在表单 extra 里，减少空白 JSON 的困惑）。 */
const TPL_EXAMPLES: Record<TplKind, string> = {
  ROLE: '角色模板示例：{ "roleCode": "DEPT_MANAGER", "roleName": "科室负责人", "portal": "HOSPITAL", "dataScope": "DEPT", "perms": ["wl:apply:approve"] }',
  APPROVAL:
    '审批流模板示例：{ "flowCode": "APPLY_STD", "flowName": "申领标准流", "bizType": "WL_APPLY", "active": true, "nodes": [{ "nodeCode": "N1", "nodeName": "科室审批", "nodeType": "APPROVE", "approveScope": "DEPT_HEAD", "roleCode": "DEPT_MANAGER" }], "routes": [{ "conditionJson": "{\\"amount\\":\\">100000\\"}", "targetNodeCode": "N2", "priority": 1 }] }',
  DICT: '字典模板示例：{ "dictCode": "URGENCY", "dictName": "紧急程度", "items": [{ "itemValue": "1", "itemLabel": "普通" }, { "itemValue": "2", "itemLabel": "加急" }] }',
  NUMBER_RULE:
    '编号规则模板示例：{ "bizType": "APPLY", "prefix": "SQ", "dateFormat": "yyyyMMdd", "seqLength": 4, "resetMode": "DAY", "step": 1 }'
};

const TPL_CODE_HINT: Record<TplKind, string> = {
  ROLE: '如 ROLE_DEPT_MANAGER',
  APPROVAL: '如 TPL_FLOW_STD',
  DICT: '如 TPL_DICT_URGENCY',
  NUMBER_RULE: '如 TPL_SEQ_APPLY'
};

/** 模板内容里可读的摘要（不展开全文，避免表格被 JSON 撑开）。 */
function payloadSummary(raw: string | null): string {
  if (!raw) return '—';
  try {
    const value = JSON.parse(raw) as unknown;
    if (Array.isArray(value)) return `数组 · ${value.length} 项`;
    if (value && typeof value === 'object') return Object.keys(value as object).join(', ');
    return String(value);
  } catch {
    return `（非 JSON）${raw.slice(0, 24)}`;
  }
}

const pretty = (raw: string | null): string | undefined => {
  if (!raw) return undefined;
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
};

// ---------------------------------------------------------------------------
// 角色模板 / 审批流模板（sys_tpl）
// ---------------------------------------------------------------------------

interface TplFormValues {
  tplCode: string;
  tplName: string;
  status: TplStatus;
  sortNo?: number;
  remark?: string;
  payloadText?: string;
}

function TplPanel({ kind }: { kind: TplKind }) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<TplRow | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm<TplFormValues>();

  const queryKey = ['platform-tpl', kind];
  const query = useQuery({ queryKey, queryFn: () => hq.platform.tpl.list(kind) });

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ status: 'ACTIVE', sortNo: 0 });
    setOpen(true);
  };

  const openEdit = (row: TplRow) => {
    setEditing(row);
    form.resetFields();
    form.setFieldsValue({
      tplCode: row.tplCode,
      tplName: row.tplName,
      status: row.status,
      sortNo: row.sortNo,
      remark: row.remark ?? undefined,
      payloadText: pretty(row.payloadJson)
    });
    setOpen(true);
  };

  const submit = async () => {
    const values = await form.validateFields();
    let payload: unknown;
    const text = values.payloadText?.trim();
    if (text) {
      try {
        payload = JSON.parse(text) as unknown;
      } catch {
        message.error('模板内容不是合法 JSON，请先修正');
        return;
      }
    }

    setSaving(true);
    try {
      await hq.platform.tpl.save({
        id: editing?.id,
        tplKind: kind,
        tplCode: values.tplCode.trim(),
        tplName: values.tplName.trim(),
        status: values.status,
        sortNo: values.sortNo ?? 0,
        remark: values.remark?.trim() || null,
        payload
      });
      message.success(editing ? '模板已更新' : '模板已新增');
      setOpen(false);
      await queryClient.invalidateQueries({ queryKey });
    } catch (err) {
      message.error(errText(err));
    } finally {
      setSaving(false);
    }
  };

  // 列定义抽成常量，便于用 sumWidth 核算横向滚动宽度（避免写死数值导致列宽被压缩）
  const columns: ColumnsType<TplRow> = [
    { title: '模板编码', dataIndex: 'tplCode', width: 200 },
    { title: '模板名称', dataIndex: 'tplName', width: 220 },
    {
      title: '状态',
      dataIndex: 'status',
      width: 90,
      render: (value: TplStatus) => statusTag(value)
    },
    { title: '排序', dataIndex: 'sortNo', width: 80 },
    {
      title: '内容摘要',
      dataIndex: 'payloadJson',
      width: 260,
      render: (value: string | null) => payloadSummary(value)
    },
    { title: '更新时间', dataIndex: 'updateAt', width: 180, render: (v: string | null) => formatDisplayTime(v) },
    {
      title: '操作',
      key: 'action',
      width: 130,
      fixed: 'right',
      render: (_, row) => (
        <Space size={8}>
          <Typography.Link onClick={() => openEdit(row)}>编辑</Typography.Link>
          <Popconfirm title={`删除模板「${row.tplName}」？`} onConfirm={() => void remove(row)}>
            <Typography.Link type="danger">删除</Typography.Link>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const remove = async (row: TplRow) => {
    try {
      await hq.platform.tpl.remove(row.id);
      message.success('模板已删除');
      await queryClient.invalidateQueries({ queryKey });
    } catch (err) {
      message.error(errText(err));
    }
  };

  return (
    <>
      <Space style={{ marginBottom: 12 }}>
        <Button type="primary" onClick={openCreate}>
          新增模板
        </Button>
        <Button onClick={() => void query.refetch()} loading={query.isFetching}>
          刷新
        </Button>
        <Typography.Text type="secondary">
          模板是"开通租户时套用"的母版快照，不参与运行期鉴权
        </Typography.Text>
      </Space>

      {query.error ? (
        <Typography.Paragraph type="danger">{errText(query.error)}</Typography.Paragraph>
      ) : null}

      <Table<TplRow>
        size="small"
        rowKey="id"
        loading={query.isLoading}
        dataSource={query.data ?? []}
        pagination={{ pageSize: 20, showSizeChanger: false }}
        scroll={{ x: sumWidth(columns) }}
        columns={columns}
      />

      <Modal
        title={editing ? `编辑模板：${editing.tplName}` : '新增模板'}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => void submit()}
        okText="保存"
        confirmLoading={saving}
        width={680}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Space size={16} style={{ display: 'flex' }}>
            <Form.Item
              name="tplCode"
              label="模板编码"
              style={{ flex: 1 }}
              rules={[
                { required: true, message: '请输入模板编码' },
                { pattern: /^[0-9A-Za-z_.-]{2,64}$/, message: '2–64 位字母/数字/下划线/点/短横线' }
              ]}
            >
              <Input placeholder={TPL_CODE_HINT[kind]} maxLength={64} />
            </Form.Item>
            <Form.Item name="status" label="状态" style={{ width: 120 }} rules={[{ required: true }]}>
              <Select options={STATUS_OPTIONS} />
            </Form.Item>
            <Form.Item name="sortNo" label="排序" style={{ width: 100 }}>
              <Input type="number" min={0} />
            </Form.Item>
          </Space>

          <Form.Item
            name="tplName"
            label="模板名称"
            rules={[{ required: true, message: '请输入模板名称' }, { max: 128, message: '不超过 128 字' }]}
          >
            <Input maxLength={128} />
          </Form.Item>

          <Form.Item
            name="payloadText"
            label="模板内容（JSON）"
            extra={TPL_EXAMPLES[kind]}
          >
            <Input.TextArea rows={10} style={{ fontFamily: 'monospace' }} placeholder="留空表示沿用原内容" />
          </Form.Item>

          <Form.Item name="remark" label="备注" rules={[{ max: 500, message: '不超过 500 字' }]}>
            <Input.TextArea rows={2} maxLength={500} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

// ---------------------------------------------------------------------------
// 指标定义（cfg_metric_def）
// ---------------------------------------------------------------------------

interface MetricFormValues {
  metricCode: string;
  metricName: string;
  metricGroup: MetricGroup;
  versionNo: string;
  unit?: string;
  higherIsBetter: boolean;
  dimensions?: string;
  calcFormula?: string;
  sourceTables?: string;
  status: TplStatus;
  remark?: string;
}

const METRIC_GROUPS: { value: MetricGroup; label: string }[] = [
  { value: 'PURCHASE', label: '采购' },
  { value: 'INVENTORY', label: '库存' },
  { value: 'BUDGET', label: '预算' },
  { value: 'DELIVERY', label: '配送' },
  { value: 'QUALITY', label: '质量' },
  { value: 'COST_DOWN', label: '降本' }
];

function MetricPanel() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<MetricDefRow | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm<MetricFormValues>();

  const queryKey = ['platform-metric-def'];
  const query = useQuery({ queryKey, queryFn: () => hq.platform.metricDef.list() });

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ metricGroup: 'PURCHASE', versionNo: 'v1', higherIsBetter: true, status: 'ACTIVE' });
    setOpen(true);
  };

  const openEdit = (row: MetricDefRow) => {
    setEditing(row);
    form.resetFields();
    form.setFieldsValue({
      metricCode: row.metricCode,
      metricName: row.metricName,
      metricGroup: row.metricGroup,
      versionNo: row.versionNo,
      unit: row.unit ?? undefined,
      higherIsBetter: row.higherIsBetter,
      dimensions: row.dimensions ?? undefined,
      calcFormula: row.calcFormula ?? undefined,
      sourceTables: row.sourceTables ?? undefined,
      status: row.status,
      remark: row.remark ?? undefined
    });
    setOpen(true);
  };

  const submit = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      await hq.platform.metricDef.save({
        id: editing?.id,
        metricCode: values.metricCode.trim(),
        metricName: values.metricName.trim(),
        metricGroup: values.metricGroup,
        versionNo: values.versionNo.trim(),
        unit: values.unit?.trim() || null,
        higherIsBetter: values.higherIsBetter,
        dimensions: values.dimensions?.trim() || null,
        calcFormula: values.calcFormula?.trim() || null,
        sourceTables: values.sourceTables?.trim() || null,
        status: values.status,
        remark: values.remark?.trim() || null
      });
      message.success(editing ? '指标已更新' : '指标已新增');
      setOpen(false);
      await queryClient.invalidateQueries({ queryKey });
    } catch (err) {
      message.error(errText(err));
    } finally {
      setSaving(false);
    }
  };

  const columns: ColumnsType<MetricDefRow> = [
    { title: '指标编码', dataIndex: 'metricCode', width: 190 },
    { title: '指标名称', dataIndex: 'metricName', width: 200 },
    {
      title: '分组',
      dataIndex: 'metricGroup',
      width: 100,
      render: (value: MetricGroup) =>
        METRIC_GROUPS.find((item) => item.value === value)?.label ?? value
    },
    { title: '口径版本', dataIndex: 'versionNo', width: 100 },
    { title: '单位', dataIndex: 'unit', width: 90, render: (v: string | null) => v ?? '—' },
    {
      title: '方向',
      dataIndex: 'higherIsBetter',
      width: 90,
      render: (value: boolean) => (value ? <Tag color="green">越高越好</Tag> : <Tag color="orange">越低越好</Tag>)
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 90,
      render: (value: TplStatus) => statusTag(value)
    },
    {
      title: '计算公式',
      dataIndex: 'calcFormula',
      width: 240,
      ellipsis: { showTitle: false },
      render: (value: string | null) => (value ? <TipText text={value} /> : '—')
    },
    {
      title: '操作',
      key: 'action',
      width: 130,
      fixed: 'right',
      render: (_, row) => (
        <Space size={8}>
          <Typography.Link onClick={() => openEdit(row)}>编辑</Typography.Link>
          <Popconfirm title={`删除指标「${row.metricName}」？`} onConfirm={() => void remove(row)}>
            <Typography.Link type="danger">删除</Typography.Link>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const remove = async (row: MetricDefRow) => {
    try {
      await hq.platform.metricDef.remove(row.id);
      message.success('指标已删除');
      await queryClient.invalidateQueries({ queryKey });
    } catch (err) {
      message.error(errText(err));
    }
  };

  return (
    <>
      <Space style={{ marginBottom: 12 }}>
        <Button type="primary" onClick={openCreate}>
          新增指标
        </Button>
        <Button onClick={() => void query.refetch()} loading={query.isFetching}>
          刷新
        </Button>
        <Typography.Text type="secondary">口径按「编码 + 版本号」唯一，改口径请升版本号</Typography.Text>
      </Space>

      {query.error ? (
        <Typography.Paragraph type="danger">{errText(query.error)}</Typography.Paragraph>
      ) : null}

      <Table<MetricDefRow>
        size="small"
        rowKey="id"
        loading={query.isLoading}
        dataSource={query.data ?? []}
        pagination={{ pageSize: 20, showSizeChanger: false }}
        scroll={{ x: sumWidth(columns) }}
        columns={columns}
      />

      <Modal
        title={editing ? `编辑指标：${editing.metricName}` : '新增指标'}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => void submit()}
        okText="保存"
        confirmLoading={saving}
        width={720}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Space size={16} style={{ display: 'flex' }}>
            <Form.Item
              name="metricCode"
              label="指标编码"
              style={{ flex: 1 }}
              rules={[
                { required: true, message: '请输入指标编码' },
                { pattern: /^[0-9A-Za-z_.-]{2,64}$/, message: '2–64 位字母/数字/下划线/点/短横线' }
              ]}
            >
              <Input placeholder="如 PURCHASE_TOTAL_AMT" maxLength={64} />
            </Form.Item>
            <Form.Item name="versionNo" label="口径版本" style={{ width: 120 }} rules={[{ required: true }]}>
              <Input placeholder="v1" maxLength={16} />
            </Form.Item>
            <Form.Item name="status" label="状态" style={{ width: 120 }} rules={[{ required: true }]}>
              <Select options={STATUS_OPTIONS} />
            </Form.Item>
          </Space>

          <Space size={16} style={{ display: 'flex' }}>
            <Form.Item
              name="metricName"
              label="指标名称"
              style={{ flex: 1 }}
              rules={[{ required: true, message: '请输入指标名称' }, { max: 128 }]}
            >
              <Input maxLength={128} />
            </Form.Item>
            <Form.Item name="metricGroup" label="分组" style={{ width: 150 }} rules={[{ required: true }]}>
              <Select options={METRIC_GROUPS} />
            </Form.Item>
            <Form.Item name="unit" label="单位" style={{ width: 120 }}>
              <Input placeholder="元 / %" maxLength={24} />
            </Form.Item>
            <Form.Item name="higherIsBetter" label="正向指标" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Space>

          <Form.Item name="calcFormula" label="计算公式">
            <Input.TextArea rows={3} placeholder="如 SUM(wl_purchase.total_amt) / 预算额 × 100%" />
          </Form.Item>
          <Space size={16} style={{ display: 'flex' }}>
            <Form.Item name="sourceTables" label="依赖源表" style={{ flex: 1 }}>
              <Input placeholder="如 wl_purchase,wl_purchase_item" maxLength={500} />
            </Form.Item>
            <Form.Item name="dimensions" label="可用维度" style={{ flex: 1 }}>
              <Input placeholder="如 ORG,DEPT,CATEGORY" maxLength={255} />
            </Form.Item>
          </Space>
          <Form.Item name="remark" label="备注" rules={[{ max: 500 }]}>
            <Input.TextArea rows={2} maxLength={500} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

// ---------------------------------------------------------------------------
// 消息模板（cfg_message_tpl）
// ---------------------------------------------------------------------------

interface MessageFormValues {
  tplCode: string;
  channel: MessageChannel;
  title?: string;
  contentTpl?: string;
  scene?: string;
  jumpPath?: string;
  enabled: boolean;
}

const CHANNELS: { value: MessageChannel; label: string }[] = [
  { value: 'INNER', label: '站内' },
  { value: 'WECHAT_MINI', label: '微信小程序' },
  { value: 'DINGTALK', label: '钉钉' },
  { value: 'SMS', label: '短信' }
];

function MessagePanel() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<MessageTplRow | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm<MessageFormValues>();

  const queryKey = ['platform-message-tpl'];
  const query = useQuery({ queryKey, queryFn: () => hq.platform.messageTpl.list() });

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ channel: 'INNER', enabled: true });
    setOpen(true);
  };

  const openEdit = (row: MessageTplRow) => {
    setEditing(row);
    form.resetFields();
    form.setFieldsValue({
      tplCode: row.tplCode,
      channel: row.channel,
      title: row.title ?? undefined,
      contentTpl: row.contentTpl ?? undefined,
      scene: row.scene ?? undefined,
      jumpPath: row.jumpPath ?? undefined,
      enabled: row.enabled
    });
    setOpen(true);
  };

  const submit = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      await hq.platform.messageTpl.save({
        id: editing?.id,
        tplCode: values.tplCode.trim(),
        channel: values.channel,
        title: values.title?.trim() || null,
        contentTpl: values.contentTpl?.trim() || null,
        scene: values.scene?.trim() || null,
        jumpPath: values.jumpPath?.trim() || null,
        enabled: values.enabled
      });
      message.success(editing ? '消息模板已更新' : '消息模板已新增');
      setOpen(false);
      await queryClient.invalidateQueries({ queryKey });
    } catch (err) {
      message.error(errText(err));
    } finally {
      setSaving(false);
    }
  };

  const columns: ColumnsType<MessageTplRow> = [
    { title: '模板编码', dataIndex: 'tplCode', width: 200 },
    {
      title: '通道',
      dataIndex: 'channel',
      width: 120,
      render: (value: MessageChannel) => CHANNELS.find((item) => item.value === value)?.label ?? value
    },
    { title: '标题', dataIndex: 'title', width: 220, ellipsis: { showTitle: false }, render: (v: string | null) => (v ? <TipText text={v} /> : '—') },
    { title: '场景', dataIndex: 'scene', width: 160, ellipsis: { showTitle: false }, render: (v: string | null) => (v ? <TipText text={v} /> : '—') },
    { title: '跳转路径', dataIndex: 'jumpPath', width: 200, ellipsis: { showTitle: false }, render: (v: string | null) => (v ? <TipText text={v} /> : '—') },
    {
      title: '状态',
      dataIndex: 'enabled',
      width: 90,
      render: (value: boolean) => (value ? <Tag color="green">启用</Tag> : <Tag>停用</Tag>)
    },
    { title: '更新时间', dataIndex: 'updateAt', width: 180, render: (v: string | null) => formatDisplayTime(v) },
    {
      title: '操作',
      key: 'action',
      width: 130,
      fixed: 'right',
      render: (_, row) => (
        <Space size={8}>
          <Typography.Link onClick={() => openEdit(row)}>编辑</Typography.Link>
          <Popconfirm title={`删除模板「${row.tplCode}」？`} onConfirm={() => void remove(row)}>
            <Typography.Link type="danger">删除</Typography.Link>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const remove = async (row: MessageTplRow) => {
    try {
      await hq.platform.messageTpl.remove(row.id);
      message.success('消息模板已删除');
      await queryClient.invalidateQueries({ queryKey });
    } catch (err) {
      message.error(errText(err));
    }
  };

  return (
    <>
      <Space style={{ marginBottom: 12 }}>
        <Button type="primary" onClick={openCreate}>
          新增消息模板
        </Button>
        <Button onClick={() => void query.refetch()} loading={query.isFetching}>
          刷新
        </Button>
        <Typography.Text type="secondary">
          同「编码 + 通道」唯一；变量占位用 {'{{变量名}}'}
        </Typography.Text>
      </Space>

      {query.error ? (
        <Typography.Paragraph type="danger">{errText(query.error)}</Typography.Paragraph>
      ) : null}

      <Table<MessageTplRow>
        size="small"
        rowKey="id"
        loading={query.isLoading}
        dataSource={query.data ?? []}
        pagination={{ pageSize: 20, showSizeChanger: false }}
        scroll={{ x: sumWidth(columns) }}
        columns={columns}
      />

      <Modal
        title={editing ? `编辑消息模板：${editing.tplCode}` : '新增消息模板'}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => void submit()}
        okText="保存"
        confirmLoading={saving}
        width={720}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Space size={16} style={{ display: 'flex' }}>
            <Form.Item
              name="tplCode"
              label="模板编码"
              style={{ flex: 1 }}
              rules={[
                { required: true, message: '请输入模板编码' },
                { pattern: /^[0-9A-Za-z_.-]{2,64}$/, message: '2–64 位字母/数字/下划线/点/短横线' }
              ]}
            >
              <Input placeholder="如 APPLY_APPROVED" maxLength={64} />
            </Form.Item>
            <Form.Item name="channel" label="通道" style={{ width: 160 }} rules={[{ required: true }]}>
              <Select options={CHANNELS} />
            </Form.Item>
            <Form.Item name="enabled" label="启用" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Space>

          <Form.Item name="title" label="标题" rules={[{ max: 255 }]}>
            <Input maxLength={255} placeholder="如 申领单已审批通过" />
          </Form.Item>
          <Form.Item name="contentTpl" label="内容模板" rules={[{ max: 4000 }]}>
            <Input.TextArea rows={4} maxLength={4000} placeholder="如 您的申领单 {{bizNo}} 已通过，预计 {{eta}} 送达" />
          </Form.Item>
          <Space size={16} style={{ display: 'flex' }}>
            <Form.Item name="scene" label="场景" style={{ flex: 1 }} rules={[{ max: 64 }]}>
              <Input placeholder="如 APPLY_RESULT" maxLength={64} />
            </Form.Item>
            <Form.Item name="jumpPath" label="跳转路径" style={{ flex: 1 }} rules={[{ max: 255 }]}>
              <Input placeholder="如 /apply/detail?id={{bizId}}" maxLength={255} />
            </Form.Item>
          </Space>
        </Form>
      </Modal>
    </>
  );
}

// ---------------------------------------------------------------------------
// 平台参数（sys_param，tenant_id=0）
// ---------------------------------------------------------------------------

interface ParamFormValues {
  paramKey: string;
  paramName: string;
  paramValue?: string;
  valueType: ParamValueType;
  module: string;
  needRestart: boolean;
  remark?: string;
}

const VALUE_TYPES: { value: ParamValueType; label: string }[] = [
  { value: 'STRING', label: 'STRING' },
  { value: 'INT', label: 'INT' },
  { value: 'DECIMAL', label: 'DECIMAL' },
  { value: 'BOOL', label: 'BOOL' },
  { value: 'JSON', label: 'JSON' }
];

function ParamPanel() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<PlatformParamRow | null>(null);
  const [saving, setSaving] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [form] = Form.useForm<ParamFormValues>();

  const queryKey = ['platform-param'];
  const query = useQuery({ queryKey, queryFn: () => hq.platform.param.list() });

  const rows = (query.data ?? []).filter((row) => {
    const kw = keyword.trim().toLowerCase();
    if (!kw) return true;
    return (
      row.paramKey.toLowerCase().includes(kw) ||
      row.paramName.toLowerCase().includes(kw) ||
      row.module.toLowerCase().includes(kw)
    );
  });

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ valueType: 'STRING', module: 'COMMON', needRestart: false });
    setOpen(true);
  };

  const openEdit = (row: PlatformParamRow) => {
    setEditing(row);
    form.resetFields();
    form.setFieldsValue({
      paramKey: row.paramKey,
      paramName: row.paramName,
      paramValue: row.paramValue ?? undefined,
      valueType: row.valueType,
      module: row.module,
      needRestart: row.needRestart,
      remark: row.remark ?? undefined
    });
    setOpen(true);
  };

  const submit = async () => {
    const values = await form.validateFields();
    if (values.valueType === 'JSON' && values.paramValue?.trim()) {
      try {
        JSON.parse(values.paramValue);
      } catch {
        message.error('参数类型为 JSON 时，参数值必须为合法 JSON');
        return;
      }
    }

    setSaving(true);
    try {
      await hq.platform.param.save({
        id: editing?.id,
        paramKey: values.paramKey.trim(),
        paramName: values.paramName.trim(),
        paramValue: values.paramValue?.trim() || null,
        valueType: values.valueType,
        module: values.module?.trim().toUpperCase() || 'COMMON',
        needRestart: values.needRestart,
        remark: values.remark?.trim() || null
      });
      message.success(editing ? '参数已更新' : '参数已新增');
      setOpen(false);
      await queryClient.invalidateQueries({ queryKey });
    } catch (err) {
      message.error(errText(err));
    } finally {
      setSaving(false);
    }
  };

  const columns: ColumnsType<PlatformParamRow> = [
    { title: '参数键', dataIndex: 'paramKey', width: 220, ellipsis: { showTitle: false }, render: (v: string) => <TipText text={v} /> },
    { title: '参数名', dataIndex: 'paramName', width: 200, ellipsis: { showTitle: false }, render: (v: string) => <TipText text={v} /> },
    {
      title: '参数值',
      dataIndex: 'paramValue',
      width: 260,
      ellipsis: { showTitle: false },
      render: (value: string | null) =>
        value ? <TipText text={<Typography.Text code>{value}</Typography.Text>} tip={value} /> : '—'
    },
    { title: '类型', dataIndex: 'valueType', width: 100 },
    { title: '模块', dataIndex: 'module', width: 120 },
    {
      title: '需重启',
      dataIndex: 'needRestart',
      width: 90,
      render: (value: boolean) => (value ? <Tag color="orange">是</Tag> : '否')
    },
    { title: '更新时间', dataIndex: 'updateAt', width: 180, render: (v: string | null) => formatDisplayTime(v) },
    {
      title: '操作',
      key: 'action',
      width: 130,
      fixed: 'right',
      render: (_, row) => (
        <Space size={8}>
          <Typography.Link onClick={() => openEdit(row)}>编辑</Typography.Link>
          <Popconfirm title={`删除参数「${row.paramKey}」？`} onConfirm={() => void remove(row)}>
            <Typography.Link type="danger">删除</Typography.Link>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const remove = async (row: PlatformParamRow) => {
    try {
      await hq.platform.param.remove(row.id);
      message.success('参数已删除');
      await queryClient.invalidateQueries({ queryKey });
    } catch (err) {
      message.error(errText(err));
    }
  };

  return (
    <>
      <Space style={{ marginBottom: 12 }} wrap>
        <Button type="primary" onClick={openCreate}>
          新增参数
        </Button>
        <Button onClick={() => void query.refetch()} loading={query.isFetching}>
          刷新
        </Button>
        <Input.Search
          allowClear
          placeholder="搜索参数键 / 名称 / 模块"
          style={{ width: 240 }}
          onSearch={setKeyword}
          onChange={(event) => {
            if (event.target.value === '') setKeyword('');
          }}
        />
        <Typography.Text type="secondary">
          平台级参数（`sys_param.tenant_id=0`）；机构级覆盖走「机构参数」页
        </Typography.Text>
      </Space>

      {query.error ? (
        <Typography.Paragraph type="danger">{errText(query.error)}</Typography.Paragraph>
      ) : null}

      <Table<PlatformParamRow>
        size="small"
        rowKey="id"
        loading={query.isLoading}
        dataSource={rows}
        pagination={{ pageSize: 20, showSizeChanger: false }}
        scroll={{ x: sumWidth(columns) }}
        columns={columns}
      />

      <Modal
        title={editing ? `编辑参数：${editing.paramKey}` : '新增平台参数'}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => void submit()}
        okText="保存"
        confirmLoading={saving}
        width={680}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Space size={16} style={{ display: 'flex' }}>
            <Form.Item
              name="paramKey"
              label="参数键"
              style={{ flex: 1 }}
              rules={[
                { required: true, message: '请输入参数键' },
                { pattern: /^[0-9A-Za-z_.-]{2,64}$/, message: '2–64 位字母/数字/下划线/点/短横线' }
              ]}
            >
              <Input placeholder="如 PLATFORM_TITLE" maxLength={64} />
            </Form.Item>
            <Form.Item name="valueType" label="类型" style={{ width: 140 }} rules={[{ required: true }]}>
              <Select options={VALUE_TYPES} />
            </Form.Item>
            <Form.Item name="needRestart" label="需重启生效" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Space>

          <Space size={16} style={{ display: 'flex' }}>
            <Form.Item
              name="paramName"
              label="参数名"
              style={{ flex: 1 }}
              rules={[{ required: true, message: '请输入参数名' }, { max: 128 }]}
            >
              <Input maxLength={128} placeholder="如 平台标题" />
            </Form.Item>
            <Form.Item name="module" label="所属模块" style={{ flex: 1 }} rules={[{ max: 64 }]}>
              <Input placeholder="COMMON" maxLength={64} />
            </Form.Item>
          </Space>

          <Form.Item name="paramValue" label="参数值" rules={[{ max: 1000 }]}>
            <Input.TextArea rows={3} maxLength={1000} placeholder="JSON 类型需填合法 JSON 文本" />
          </Form.Item>
          <Form.Item name="remark" label="备注" rules={[{ max: 500 }]}>
            <Input.TextArea rows={2} maxLength={500} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

/**
 * 平台基础数据（`sys:platform:config` + `portal=SYSTEM`，docs/07 §7.3 #8）：
 * 角色模板 / 审批流模板（`sys_tpl`）、指标定义（`cfg_metric_def`）、
 * 消息模板（`cfg_message_tpl`）、平台参数（`sys_param`）。
 */
export default function Platform() {
  return (
    <div>
      <Typography.Title level={4} style={{ marginTop: 0 }}>
        平台基础数据
      </Typography.Title>
      <Typography.Paragraph type="secondary">
        租户开通时套用的母版与平台级运行参数；所有写操作由 `sys:platform:config` 门禁，且要求 SYSTEM 门户。
      </Typography.Paragraph>

      <Card size="small">
        <Tabs
          items={[
            { key: 'role', label: '角色模板', children: <TplPanel kind="ROLE" /> },
            { key: 'approval', label: '审批流模板', children: <TplPanel kind="APPROVAL" /> },
            { key: 'dict', label: '字典模板', children: <TplPanel kind="DICT" /> },
            { key: 'number-rule', label: '编号规则模板', children: <TplPanel kind="NUMBER_RULE" /> },
            { key: 'metric', label: '指标定义', children: <MetricPanel /> },
            { key: 'message', label: '消息模板', children: <MessagePanel /> },
            { key: 'param', label: '平台参数', children: <ParamPanel /> }
          ]}
        />
      </Card>
    </div>
  );
}
